import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import mqtt from 'mqtt';
import { environment } from 'src/environments/environment';

export interface MqttMessage {
  topic: string;
  payload: any;
  timestamp: Date;
  qos: number;
  retain: boolean;
}

export interface MqttConnectionStatus {
  connected: boolean;
  connecting: boolean;
  error?: string;
  lastConnectedAt?: Date;
  lastDisconnectedAt?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class MqttService {
  private client: any | null = null;
  private messagesSubject = new Subject<MqttMessage>();
  private connectionStatusSubject = new BehaviorSubject<MqttConnectionStatus>({
    connected: false,
    connecting: false
  });

  public messages$: Observable<MqttMessage> = this.messagesSubject.asObservable();
  public connectionStatus$: Observable<MqttConnectionStatus> = this.connectionStatusSubject.asObservable();

  constructor() {}

  /**
   * Connette al broker MQTT via WebSocket
   */
  connect(options?: {
    username?: string;
    password?: string;
    clientId?: string;
  }): void {
    if (this.client) {
      console.warn('[MQTT Service] Already connected or connecting');
      return;
    }

    const brokerUrl = environment.ruleEngine.mqttBrokerUrl;
    console.log('[MQTT Service] Connecting to broker:', brokerUrl);

    this.updateStatus({ connected: false, connecting: true });

    const mqttOptions: any = {
      clientId: options?.clientId || `4agri-gui-${Math.random().toString(16).substr(2, 8)}`,
      clean: true,
      reconnectPeriod: 5000,
      connectTimeout: 30000,
    };

    // Aggiungi credenziali se fornite
    if (options?.username) {
      mqttOptions.username = options.username;
    }
    if (options?.password) {
      mqttOptions.password = options.password;
    }

    try {
      this.client = mqtt.connect(brokerUrl, mqttOptions);

      // Event: Connected
      this.client.on('connect', () => {
        console.log('[MQTT Service] Connected to broker');
        this.updateStatus({
          connected: true,
          connecting: false,
          lastConnectedAt: new Date(),
          error: undefined
        });
      });

      // Event: Error
      this.client.on('error', (err: any) => {
        console.error('[MQTT Service] Connection error:', err);
        this.updateStatus({
          connected: false,
          connecting: false,
          error: err.message
        });
      });

      // Event: Offline
      this.client.on('offline', () => {
        console.warn('[MQTT Service] Broker offline');
        this.updateStatus({
          connected: false,
          connecting: false,
          lastDisconnectedAt: new Date()
        });
      });

      // Event: Reconnect
      this.client.on('reconnect', () => {
        console.log('[MQTT Service] Reconnecting...');
        this.updateStatus({
          connected: false,
          connecting: true
        });
      });

      // Event: Close
      this.client.on('close', () => {
        console.log('[MQTT Service] Connection closed');
        this.updateStatus({
          connected: false,
          connecting: false,
          lastDisconnectedAt: new Date()
        });
      });

      // Event: Message
      this.client.on('message', (topic: string, payload: Buffer, packet: any) => {
        try {
          const message: MqttMessage = {
            topic,
            payload: this.parsePayload(payload),
            timestamp: new Date(),
            qos: packet.qos,
            retain: packet.retain
          };

          console.log('[MQTT Service] Message received:', message);
          this.messagesSubject.next(message);
        } catch (err) {
          console.error('[MQTT Service] Error processing message:', err);
        }
      });

    } catch (err: any) {
      console.error('[MQTT Service] Failed to connect:', err);
      this.updateStatus({
        connected: false,
        connecting: false,
        error: err.message
      });
    }
  }

  /**
   * Disconnette dal broker MQTT
   */
  disconnect(): void {
    if (this.client) {
      console.log('[MQTT Service] Disconnecting from broker');
      this.client.end();
      this.client = null;
      this.updateStatus({
        connected: false,
        connecting: false,
        lastDisconnectedAt: new Date()
      });
    }
  }

  /**
   * Subscribe a uno o più topic
   */
  subscribe(topics: string | string[], qos: 0 | 1 | 2 = 1): void {
    if (!this.client) {
      console.error('[MQTT Service] Cannot subscribe: not connected');
      return;
    }

    const topicArray = Array.isArray(topics) ? topics : [topics];

    topicArray.forEach(topic => {
      this.client!.subscribe(topic, { qos }, (err: any) => {
        if (err) {
          console.error(`[MQTT Service] Subscribe error for topic ${topic}:`, err);
        } else {
          console.log(`[MQTT Service] Subscribed to topic: ${topic}`);
        }
      });
    });
  }

  /**
   * Unsubscribe da uno o più topic
   */
  unsubscribe(topics: string | string[]): void {
    if (!this.client) {
      console.error('[MQTT Service] Cannot unsubscribe: not connected');
      return;
    }

    const topicArray = Array.isArray(topics) ? topics : [topics];

    topicArray.forEach(topic => {
      this.client!.unsubscribe(topic, (err: any) => {
        if (err) {
          console.error(`[MQTT Service] Unsubscribe error for topic ${topic}:`, err);
        } else {
          console.log(`[MQTT Service] Unsubscribed from topic: ${topic}`);
        }
      });
    });
  }

  /**
   * Verifica se è connesso
   */
  isConnected(): boolean {
    return this.client?.connected || false;
  }

  /**
   * Ottiene lo stato corrente della connessione
   */
  getConnectionStatus(): MqttConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  /**
   * Aggiorna lo stato della connessione
   */
  private updateStatus(status: Partial<MqttConnectionStatus>): void {
    const currentStatus = this.connectionStatusSubject.value;
    this.connectionStatusSubject.next({ ...currentStatus, ...status });
  }

  /**
   * Parse del payload (JSON o text)
   */
  private parsePayload(payload: Buffer): any {
    const text = payload.toString();
    
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  /**
   * Cleanup quando il servizio viene distrutto
   */
  ngOnDestroy(): void {
    this.disconnect();
  }
}
